import { AuthState } from "../models/auth/auth-state";
import { CookieUtils } from "../utils/cookie-utils";
import { patchState, signalStore, withHooks, withMethods, withState } from "@ngrx/signals";
import { BehaviorSubject, EMPTY, Observable, finalize, tap } from "rxjs";
import { TokenViewModel } from "@playcharge/common/models/backend/auth/token-view-model";
import { inject } from "@angular/core";
import { ApiAuthService } from "../services/api-auth.service";

export type AuthStore = InstanceType<typeof AuthStore>;

export const AuthStore = signalStore(
    { providedIn: "root", protectedState: false },
    withState<AuthState>({
        isAuthorized: false,
        isRefreshInProgress: false,
        isRefreshCompleted: new BehaviorSubject<boolean>(false),
    }),
    withMethods((store, apiAuthService = inject(ApiAuthService)) => ({
        signIn(email: string, password: string): Observable<TokenViewModel> {
            return apiAuthService
                .signIn(email, password)
                .pipe(tap(() => patchState(store, { isAuthorized: true })));
        },
        refreshToken(): Observable<TokenViewModel> {
            if (store.isRefreshInProgress()) {
                return EMPTY;
            }

            patchState(store, { isRefreshInProgress: true });

            return apiAuthService.refreshToken().pipe(
                tap(() => patchState(store, { isAuthorized: true })),
                finalize(() => patchState(store, { isRefreshInProgress: false })),
            );
        },
        logout(): Observable<void> {
            return apiAuthService.logout().pipe(
                tap(() => {
                    patchState(store, { isAuthorized: false });
                }),
            );
        },
        setAuthStatus(status: boolean): void {
            patchState(store, { isAuthorized: status });
        },
        setRefreshStatus(status: boolean): void {
            patchState(store, { isRefreshInProgress: status });
        },
        getInitialAuthStatus(): boolean {
            const expirationCookie: number = Number(
                CookieUtils.getCookie("refresh_token_expiry_date") ?? 0,
            );

            if (expirationCookie === 0) {
                return false;
            }

            return expirationCookie * 1000 > Date.now();
        },
    })),
    withHooks((store) => ({
        onInit(): void {
            patchState(store, { isAuthorized: store.getInitialAuthStatus() });
        },
    })),
);
